import React from 'react';
import { Building2, Gamepad2, Settings2 } from 'lucide-react';

/**
 * PartnerBadges — Composant réutilisable pour afficher les badges
 * supplier_type + is_partner sur toute interface partenaire.
 * 
 * RÈGLE UX: La rosette est ICON-ONLY (aucun texte).
 * L'absence de rosette = non partenaire commercial.
 * 
 * Props:
 *   supplierType: 'distributor' | 'publisher' | 'other'
 *   isPartner: boolean
 *   size: 'sm' | 'md' | 'lg' | 'card' | 'product' | 'detail' (default: 'md')
 *   showTypeLabel: boolean (default: true)
 *   rosetteOnly: boolean (default: false) — n'affiche que la rosette, pas le badge type
 *   className: string (additional container classes)
 */

const SUPPLIER_TYPE_CONFIG = {
  distributor: {
    label: 'Distributeur',
    icon: Building2,
    color: 'bg-blue-50 text-blue-700 border-blue-200'
  },
  publisher: {
    label: 'Éditeur',
    icon: Gamepad2,
    color: 'bg-purple-50 text-purple-700 border-purple-200'
  },
  other: {
    label: 'Autre',
    icon: Settings2,
    color: 'bg-gray-50 text-gray-600 border-gray-200'
  }
};

const SIZE_CONFIG = {
  sm: { badge: 'px-1.5 py-0.5 text-[10px]', icon: 'h-2.5 w-2.5', rosette: 'w-4 h-4' },
  md: { badge: 'px-2.5 py-1 text-xs', icon: 'h-3 w-3', rosette: 'w-5 h-5' },
  lg: { badge: 'px-3 py-1.5 text-sm', icon: 'h-3.5 w-3.5', rosette: 'w-6 h-6' },
  // Tailles contextuelles selon spec
  card: { badge: 'px-2 py-0.5 text-[11px]', icon: 'h-3 w-3', rosette: 'w-8 h-8' },      // 28-36px → PartnerCard listing
  product: { badge: 'px-1.5 py-0.5 text-[10px]', icon: 'h-2.5 w-2.5', rosette: 'w-4 h-4' }, // 12-16px → ProductCard
  detail: { badge: 'px-2 py-0.5 text-xs', icon: 'h-3 w-3', rosette: 'w-[18px] h-[18px]' }  // 14-18px → ProductDetail
};

/**
 * RosetteIcon — SVG médaille rosette pour "Partenaire commercial"
 * Cercle + collerette + rubans — marqueur de confiance B2B
 * ICON-ONLY: aucun texte associé
 */
const RosetteIcon = ({ className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    {/* Rubans */}
    <path d="M8.5 15.5L6 22l3-1.5L10.5 22l-1-3.5" fill="#d97706" opacity="0.8" />
    <path d="M15.5 15.5L18 22l-3-1.5L13.5 22l1-3.5" fill="#d97706" opacity="0.8" />
    {/* Collerette (pétales) */}
    <circle cx="12" cy="11" r="7.5" fill="#fbbf24" opacity="0.3" />
    <path d="M12 3.5C12 3.5 14.5 5.5 14.5 8s-1 4-2.5 5.5C10.5 12 9.5 10.5 9.5 8S12 3.5 12 3.5z" fill="#f59e0b" opacity="0.4" />
    <path d="M5.5 9C5.5 9 8 8 10 9.5s2.5 4 2.5 4-2.5 0-4.5-1S5.5 9 5.5 9z" fill="#f59e0b" opacity="0.4" />
    <path d="M18.5 9C18.5 9 16 8 14 9.5s-2.5 4-2.5 4 2.5 0 4.5-1S18.5 9 18.5 9z" fill="#f59e0b" opacity="0.4" />
    {/* Cercle central */}
    <circle cx="12" cy="11" r="4.5" fill="#f59e0b" />
    <circle cx="12" cy="11" r="3.2" fill="#fbbf24" />
    {/* Check mark */}
    <path d="M10 11l1.5 1.5L14 9.5" stroke="#92400e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

/**
 * Résout les capabilities d'un partenaire (double lecture)
 * Priorité: capabilities[] > supplier_type (fallback)
 * Garantie: toujours au moins ["other"]
 */
const resolveCapabilities = (capabilities, supplierType) => {
  if (capabilities && Array.isArray(capabilities) && capabilities.length > 0) {
    return capabilities;
  }
  const st = supplierType || 'other';
  return [st];
};

/**
 * Retourne la capability principale pour affichage primaire
 */
const getPrimaryCapability = (capabilities) => {
  const priority = ['distributor', 'publisher', 'other'];
  return priority.find(c => capabilities.includes(c)) || 'other';
};

/**
 * PartnerBadges
 * 
 * Props:
 *   maxBadges: number (default: 99) — max badges affichés, surplus affiché en "+n"
 *                Utiliser 2 pour les cartes, 99 pour les fiches détail
 */
const PartnerBadges = ({
  supplierType = 'other',
  capabilities = null,
  isPartner = false,
  size = 'md',
  showTypeLabel = true,
  rosetteOnly = false,
  maxBadges = 99,
  className = ''
}) => {
  const resolvedCaps = resolveCapabilities(capabilities, supplierType);
  const sizeConfig = SIZE_CONFIG[size] || SIZE_CONFIG.md;

  // Mode rosette-only : juste l'icône, pas de badge type
  if (rosetteOnly) {
    if (!isPartner) return null;
    return (
      <span
        className={`inline-flex items-center ${className}`}
        title="Partenaire commercial"
        data-testid="badge-partner-official"
      >
        <RosetteIcon className={sizeConfig.rosette} />
      </span>
    );
  }

  // Filtrer "other" pour n'afficher que les badges métier significatifs
  const displayCaps = resolvedCaps.filter(c => c !== 'other');
  // Si aucune capability métier → afficher "Autre"
  const badgesToShow = displayCaps.length > 0 ? displayCaps : ['other'];
  const visibleBadges = badgesToShow.slice(0, maxBadges);
  const overflow = badgesToShow.length - maxBadges;

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid="partner-badges">
      {/* Badges capabilities */}
      {showTypeLabel && visibleBadges.map(cap => {
        const config = SUPPLIER_TYPE_CONFIG[cap] || SUPPLIER_TYPE_CONFIG.other;
        const Icon = config.icon;
        return (
          <span
            key={cap}
            className={`inline-flex items-center gap-1 border rounded-full font-medium ${config.color} ${sizeConfig.badge}`}
            data-testid={`badge-supplier-type-${cap}`}
          >
            <Icon className={sizeConfig.icon} />
            {config.label}
          </span>
        );
      })}

      {/* Overflow "+n" */}
      {overflow > 0 && (
        <span className={`inline-flex items-center border rounded-full font-medium bg-gray-50 text-gray-500 border-gray-200 ${sizeConfig.badge}`}>
          +{overflow}
        </span>
      )}

      {/* Partenaire commercial — rosette ICON-ONLY */}
      {isPartner && (
        <span
          className="inline-flex items-center"
          title="Partenaire commercial"
          data-testid="badge-partner-official"
        >
          <RosetteIcon className={sizeConfig.rosette} />
        </span>
      )}
    </div>
  );
};

export { RosetteIcon, SUPPLIER_TYPE_CONFIG, resolveCapabilities, getPrimaryCapability };
export default PartnerBadges;
